/**
 * Cast bar + spell name for a player frame (#192). Rendered directly under the
 * HP/resource bars in BOTH OwnFrame (self) and TargetFrame (target/enemy) so a
 * cast-time spell shows a filling bar wherever that unit has a frame.
 *
 * Shows ONLY cast-time spells: {@link castProgress} returns `null` for idle,
 * instant, and off-GCD casts (`endTick <= startTick`), and this renders nothing
 * in those cases — no empty bar. Channels don't exist yet (#185); revisit then.
 *
 * PURELY COSMETIC / CLIENT-SIDE. Reads only the already-wired per-entity `cast`
 * state (`WireEntity.cast`) — no sim, no protocol, no hash, no SIM_VERSION.
 *
 * Champion resolution (#420): the spell NAME resolves against whatever
 * `championId` the caller supplies. Every caller now passes the ACTUAL
 * entity's `championId` (`WireEntity.championId`, #420) — `OwnFrame` uses
 * `state.ownChampionId` (the local player's own pick, resolved earlier in the
 * render than the wire snapshot), `TargetFrame`/`FriendlyTargetFrame` use the
 * target entity's own wire-carried `championId`. An unresolved slot renders
 * the bar with no name rather than a guessed label.
 */

import type { ReactElement } from "react";
import type { ChampionId, WireSimState } from "@pit/protocol";
import { abilityLabel } from "../champions/abilityLabels.js";
import { castProgress } from "./hud-util.js";

/** Cast-fill color — matches the ActionBar casting accent + own CastBar,
 *  distinct from the health-fraction HP palette. */
const CAST_FILL = "#d5a640";

export function FrameCastBar({
  cast,
  tick,
  championId,
  testIdPrefix,
}: {
  readonly cast: WireSimState["entities"][number]["cast"];
  readonly tick: number;
  readonly championId: ChampionId;
  /** data-testid prefix, e.g. "own-frame" → "own-frame-cast-bar"/"-cast-name". */
  readonly testIdPrefix: string;
}): ReactElement | null {
  if (cast === null) return null;
  const f = castProgress(cast, tick);
  if (f === null) return null;
  const name = abilityLabel(championId, cast.slot) ?? "";
  return (
    <div style={{ marginTop: 4 }}>
      <div
        data-testid={`${testIdPrefix}-cast-bar`}
        data-fill={String(f)}
        style={{
          width: "100%",
          height: 6,
          background: "#111",
          border: "1px solid #333",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${f * 100}%`,
            height: "100%",
            background: CAST_FILL,
          }}
        />
      </div>
      {name === "" ? null : (
        <div
          data-testid={`${testIdPrefix}-cast-name`}
          style={{ fontSize: 11, marginTop: 2, color: "#d5c9a0" }}
        >
          {name}
        </div>
      )}
    </div>
  );
}
